import { Link } from "react-router-dom";
import { useLang } from "@/i18n/LanguageContext";
import { ArrowRight, HandHeart, Waves, Sun, Flower, Sparkles, Salad, CloudSun, CalendarHeart } from "lucide-react";
import { PageHero } from "@/components/PageHero";
import { Reveal, SectionHeading } from "@/components/motion";
import { CtaBand } from "@/components/CtaBand";
import { LeafMark } from "@/components/LeafMark";
import { usePageMeta } from "@/lib/seo";
import { IMG } from "@/lib/site";

const EXPERIENCE_ICONS = [HandHeart, Sun, Waves, Flower, Sparkles, Salad, CloudSun, CalendarHeart];

export const Ayurveda = () => {
    const {  t, tHtml, site: siteData } = useLang();
    const { AYURVEDA_EXPERIENCES, AYURVEDA_JOURNEY } = siteData;
    usePageMeta(
        t("meta.ayurveda.title", "Ayurveda in Kerala, India — Traditional Ayurvedic Wellness"),
        t("meta.ayurveda.desc", "Discover traditional Ayurvedic care and rejuvenation experiences in India, coordinated around your needs — consultations, Panchakarma, therapies and retreats, starting in Kerala.")
    );
    return (
        <>
            <PageHero
                eyebrow={t("ayurveda.eyebrow", "Ayurveda")}
                title={
                    <>
                        Ayurveda,{" "}
                        <span className="italic text-pine">rooted in tradition.</span>
                    </>
                }
                intro={t("ayurveda.intro", "Discover traditional Ayurvedic care and rejuvenation experiences in India, coordinated around your needs and journey.")}
                testid="ayurveda-hero"
            />

            {/* A. What is Ayurveda? */}
            <section className="section-pad pt-10" data-testid="what-is-ayurveda">
                <div className="container-site grid lg:grid-cols-2 gap-6 lg:gap-8 items-stretch">
                    <Reveal className="h-full">
                        <div className="h-full min-h-[300px] rounded-2xl overflow-hidden shadow-[0_30px_70px_rgba(6,60,53,0.14)]">
                            <img
                                src={IMG.keralaArchitecture}
                                alt="Traditional Kerala architecture with a tiled roof and warm lamps beside a calm courtyard"
                                data-testid="ayurveda-tradition-image"
                                className="w-full h-full object-cover"
                            />
                        </div>
                    </Reveal>
                    <Reveal delay={0.1} className="h-full">
                        <div className="h-full bg-forest text-ivory rounded-2xl p-8 sm:p-10 flex flex-col justify-center">
                            <LeafMark className="w-7 h-7 text-natural" />
                            <h2 className="mt-5 font-serif font-medium text-2xl sm:text-3xl leading-snug">
                                {t("ayurveda.text1", "What is Ayurveda?")}
                            </h2>
                            <p className="mt-4 text-sm leading-relaxed text-ivory/70">
                                {t("ayurveda.text2", "Ayurveda is a traditional system of health and wellbeing that originated in India several thousand years ago. Its approach is individual: it looks at the whole person — constitution, digestion, daily rhythm, sleep, nutrition and state of mind — rather than a symptom alone.")}
                            </p>
                            <p className="mt-4 text-sm leading-relaxed text-ivory/70">
                                {t("ayurveda.text3", "Today, Ayurveda is practised across India in dedicated centres and teaching institutions, and in Kerala especially it remains part of everyday life. For visitors, it offers a tradition of rest, nourishment and renewal.")}
                            </p>
                            <p className="mt-4 text-xs leading-relaxed text-ivory/55">
                                {t("ayurveda.text4", "Ayurveda complements — it does not replace — modern medical care. Medical conditions, medications and treatment decisions should always be discussed with qualified healthcare professionals.")}
                            </p>
                        </div>
                    </Reveal>
                </div>
            </section>

            {/* B. Ayurvedic experiences */}
            <section className="section-pad bg-white" data-testid="ayurveda-experiences">
                <div className="container-site">
                    <SectionHeading
                        index="—"
                        eyebrow={t("ayurveda.eyebrow", "Ayurvedic Experiences")}
                        title={
                            <>
                                Ways to experience{" "}
                                <span className="italic text-pine">the tradition.</span>
                            </>
                        }
                        intro={t("ayurveda.intro", "From a first consultation to longer retreats — experiences are shaped by qualified practitioners around the individual.")}
                    />
                    <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
                        {AYURVEDA_EXPERIENCES.map((item, i) => {
                            const Icon = EXPERIENCE_ICONS[i % EXPERIENCE_ICONS.length];
                            return (
                                <Reveal key={item.title} delay={i * 0.05} className="h-full">
                                    <div
                                        data-testid={`ayurveda-experience-${i + 1}`}
                                        className="h-full bg-ivory border border-pine/10 rounded-2xl p-7 card-lift"
                                    >
                                        <span className="flex items-center justify-center w-11 h-11 rounded-xl bg-sage text-forest">
                                            <Icon className="w-5 h-5" strokeWidth={1.5} />
                                        </span>
                                        <h3 className="mt-5 font-serif font-medium text-xl text-forest leading-snug">
                                            {item.title}
                                        </h3>
                                        <p className="mt-2 text-sm leading-relaxed text-moss">{item.desc}</p>
                                    </div>
                                </Reveal>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* C. Ayurveda in Kerala */}
            <section className="section-pad bg-sage/50" data-testid="ayurveda-kerala">
                <div className="container-site">
                    <SectionHeading
                        index="—"
                        eyebrow={t("ayurveda.eyebrow", "Ayurveda in Kerala")}
                        title={t("ayurveda.title1", "Kerala — a living tradition of Ayurveda.")}
                        intro={t("ayurveda.intro", "Kerala has a longstanding Ayurvedic tradition — its family lineages, educational institutions and the place of Ayurveda in daily life make it an important destination for people seeking Ayurvedic and wellness experiences. Its climate and abundant medicinal plants have historically shaped the practice here.")}
                    />
                    <div className="mt-12 grid lg:grid-cols-2 gap-6">
                        <Reveal>
                            <div className="rounded-2xl overflow-hidden h-72 sm:h-[420px] shadow-[0_30px_70px_rgba(6,60,53,0.14)]">
                                <img
                                    src={IMG.keralaMist}
                                    alt="Mist rising over the green Western Ghats mountains in Kerala"
                                    data-testid="ayurveda-kerala-image"
                                    className="w-full h-full object-cover"
                                    loading="lazy"
                                />
                            </div>
                        </Reveal>
                        <Reveal delay={0.1}>
                            <div className="rounded-2xl overflow-hidden h-72 sm:h-[420px] shadow-[0_30px_70px_rgba(6,60,53,0.14)]">
                                <img
                                    src={IMG.journeyRecovery}
                                    alt="A serene courtyard with lush greenery in Kerala"
                                    data-testid="ayurveda-kerala-image-2"
                                    className="w-full h-full object-cover"
                                    loading="lazy"
                                />
                            </div>
                        </Reveal>
                    </div>
                    <Reveal delay={0.1}>
                        <p className="mt-8 text-xs leading-relaxed text-moss max-w-2xl">
                            {t("ayurveda.text5", "Illustrative imagery of Kerala's landscapes and environments — shown to reflect the character of the region, not to depict any specific centre or practitioner.")}
                        </p>
                    </Reveal>
                </div>
            </section>

            {/* D. Choosing the right provider */}
            <section className="section-pad bg-white" data-testid="ayurveda-providers">
                <div className="container-site">
                    <div className="max-w-3xl">
                        <SectionHeading
                            index="—"
                            eyebrow={t("ayurveda.eyebrow", "Choosing the Right Provider")}
                            title={t("ayurveda.title2", "Careful choices, made carefully.")}
                        />
                        <Reveal delay={0.05}>
                            <p className="mt-6 text-sm sm:text-[15px] leading-relaxed text-moss">
                                {t("ayurveda.text6", "Where you stay — and who guides your program — matters. UniversHeal aims to work with appropriately qualified practitioners and verified centres, and reviews each provider before it is ever suggested to a guest.")}
                            </p>
                        </Reveal>
                        <Reveal delay={0.1}>
                            <p className="mt-4 text-sm sm:text-[15px] leading-relaxed text-moss">
                                {t("ayurveda.text7", "No centre is presented as a UniversHeal partner unless a formal relationship has been established. When you enquire, we share suitable options with honest information — and the choice remains yours.")}
                            </p>
                        </Reveal>
                    </div>
                </div>
            </section>

            {/* E. Your Ayurvedic journey */}
            <section className="section-pad bg-ivory" data-testid="ayurveda-journey">
                <div className="container-site">
                    <SectionHeading
                        index="—"
                        eyebrow={t("ayurveda.eyebrow", "Your Ayurvedic Journey")}
                        title={t("ayurveda.title3", "Six steps, unhurried.")}
                    />
                    <div className="mt-12 max-w-4xl">
                        {AYURVEDA_JOURNEY.map((step, i) => (
                            <Reveal key={step.number} delay={i * 0.05}>
                                <div
                                    data-testid={`ayurveda-journey-step-${i + 1}`}
                                    className="border-t border-pine/15 py-7 grid grid-cols-[3.5rem_1fr] gap-4 sm:gap-10"
                                >
                                    <span className="font-serif italic text-3xl text-natural leading-none pt-1">
                                        {step.number}
                                    </span>
                                    <div>
                                        <h3 className="font-serif font-medium text-2xl text-forest leading-snug">
                                            {step.title}
                                        </h3>
                                        <p className="mt-2 text-sm leading-relaxed text-moss max-w-2xl">{step.desc}</p>
                                    </div>
                                </div>
                            </Reveal>
                        ))}
                    </div>
                    <Reveal delay={0.1}>
                        <p className="mt-8 text-xs leading-relaxed text-moss max-w-2xl">
                            {t("ayurveda.text8", "Wellness and Ayurvedic services may vary by provider and individual circumstances. Users should discuss medical conditions, medications and treatment decisions with qualified healthcare professionals.")}
                        </p>
                    </Reveal>
                </div>
            </section>

            <CtaBand
                headline={t("ayurveda.headline", "A tradition worth experiencing properly.")}
                text={t("ayurveda.text", "Tell us what you are looking for — we will help you explore suitable Ayurvedic experiences in India.")}
            />
        </>
    );
};
